import apiClient from '../client';
import type { ProductSchema } from '@appTypes';
import readXlsxFile from 'read-excel-file/browser';

// Products API
export type { ProductSchema } from '@appTypes';

/**
 * Fetch all products (non-paginated)
 */
export async function fetchProducts(): Promise<{ [key: string]: ProductSchema } | undefined> {
  const response = await apiClient.get('/admin/products', {
    params: { paginated: false },
  });
  return response.data;
}

/**
 * Fetch paginated products
 */
export async function fetchPaginatedProducts(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: ProductSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/admin/products', {
    params: { paginated: true, lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

/**
 * Get a specific product by ID
 */
export async function getProductById(params: { productId: string }): Promise<ProductSchema | undefined> {
  const response = await apiClient.get(`/admin/products/${params.productId}`);
  return response.data;
}

/**
 * Create a new product
 */
export async function addProduct(params: {
  productDescription: { [key: string]: string };
  productName: { [key: string]: string };
  productPicturesURLs: string[];
  productRewardValue: number;
  brandId?: string;
}): Promise<ProductSchema> {
  const response = await apiClient.post('/admin/products', params);
  return response.data;
}

/**
 * Update an existing product
 */
export async function updateProduct(params: {
  product: ProductSchema;
  newData?: Partial<ProductSchema>;
}): Promise<ProductSchema> {
  const productId = params.product.productId;
  const { productId: _pid, productCreationDate: _pcd, ...updateData } = (params.newData || params.product) as any;

  const response = await apiClient.put(`/admin/products/${productId}`, updateData);
  return response.data;
}

/**
 * Delete a product
 */
export async function deleteProduct(params: { product: ProductSchema }): Promise<void> {
  const productId = params.product.productId;
  await apiClient.delete(`/admin/products/${productId}`);
}

/**
 * Check if a product can be deleted
 */
export async function canDeleteProduct(params: { product: ProductSchema }): Promise<boolean> {
  const productId = params.product.productId;
  const response = await apiClient.get(`/admin/products/${productId}/can-delete`);
  return response.data.canDelete;
}

/**
 * Toggle product archive status
 */
export async function toggleArchiveProduct(params: { product: ProductSchema }): Promise<boolean> {
  const productId = params.product.productId;
  const response = await apiClient.post(`/admin/products/${productId}/archive`);
  return response.data.isArchived;
}

/**
 * Opens a file picker, reads the selected .xlsx/.xls file,
 * and returns the list of barcodes from the "codeb" column.
 * Returns undefined if the user cancels the picker.
 */
export async function importProductUnitsFile(): Promise<Array<string> | undefined> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(undefined);
        return;
      }

      try {
        // v8 API: readXlsxFile returns [{ sheet, data }] where data is a 2D array
        const sheets = await readXlsxFile(file);
        const data = sheets[0].data; // rows as arrays of cell values

        // Find the "codeb" column index from the header row
        const header = data[0] as (string | null)[];
        const codebIndex = header.findIndex(
          (cell) => typeof cell === 'string' && cell.trim().toLowerCase() === 'codeb'
        );

        if (codebIndex === -1) {
          throw new Error('Colonne "codeb" introuvable dans le fichier Excel.');
        }

        // Extract barcodes from all rows after the header
        const barcodes = data
          .slice(1)
          .map((row) => String(row[codebIndex] ?? '').trim())
          .filter((c) => c.length > 0);

        resolve(barcodes);
      } catch (err) {
        reject(err);
      }
    };

    // Resolve undefined if the user closes the picker without selecting
    input.oncancel = () => resolve(undefined);

    input.click();
  });
}

/**
 * Add product units (barcodes) to a product
 */
export async function addProductUnits(params: {
  productId: string;
  unitBarcodes: string[];
}): Promise<any[]> {
  const response = await apiClient.post(`/admin/products/${params.productId}/units`, {
    unitBarcodes: params.unitBarcodes,
  });
  return response.data;
}

export type ProductUnitRecord = {
  unitBarcode: string;
  productId: string;
  unitCreationDate: string;
  status: 'ScannedAndValidated' | 'Unscanned';
};

/**
 * Fetch paginated product units for a product
 */
export async function fetchProductUnits(params: {
  productId: string;
  lastDocumentId?: string;
  limit?: number;
  search?: string;
}): Promise<{ data: ProductUnitRecord[]; lastDocumentId?: string }> {
  const response: any = await apiClient.get(`/admin/products/${params.productId}/units`, {
    params: {
      lastDocumentId: params.lastDocumentId,
      limit: params.limit ?? 100,
      search: params.search || undefined,
    },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}
